import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import * as Maths3 from "oc-threejs/Maths3";
import { Aufbau3d } from "../arrangement3d/Aufbau3d";
import { Box3, Color, Sphere, Vector2, Vector3 } from "three";
import { v2, v3 } from "oc-threejs/short3";
import * as Commons from "oc-commons/Commons";
import { MAufbauten } from "../../metamodell";
import { Tree } from "./lichtleistenFunctions";
import { pfostenbreiteCirca } from "./constants";

export class Aufbau extends Jaco {
  isAufbau = true;

  Key: string = null;
  aufbauMeta: MAufbauten = null;
  size: Vector3 = v3();
  drehung: number = 0;
  fixedSize: boolean = false;

  hatKollision = false; // flag for collision detection with Überstand

  /** nur für Trafos */
  color: Color;
  /** nur für Trafos*/
  tree: Tree;
  /** nur für Trafos*/
  treeIndex: number;

  touchingCornersInLocalCoords: Vector3[] = [];

  constructor(
    parent: Jaco,
    {
      Key = null,
      aufbauMeta = null,
      position = v3(),
      size = v3(),
      drehung = 0,
      fixedSize = false,
    }: Partial<{
      Key: string;
      aufbauMeta: MAufbauten;
      position: Vector3;
      size: Vector3;
      drehung: number;
      fixedSize: boolean;
    }> = {}
  ) {
    super(parent);
    this.Key = Key;
    this.aufbauMeta = aufbauMeta;
    this.position.copy(position);
    this.size.copy(size);
    this.drehung = drehung;
    this.quaternion.setFromAxisAngle(Maths3.vector.y, this.drehung);
    this.fixedSize = fixedSize;
  }

  isTrafo() {
    return this.aufbauMeta.Key === "Trafo";
  }

  update3d() {
    super.update3d();
    this.quaternion.setFromAxisAngle(Maths3.vector.y, this.drehung);
    this.touchingCornersInLocalCoords = this.calculateContactPositions();
  }

  createJaco3d() {
    return new Aufbau3d(this);
  }

  getGridPoint(point2: Vector2): Vector2 {
    const size2 = Maths3.v3To2xz(this.size);
    const position2 = Maths3.v3To2xz(this.position);
    return point2.clone().multiply(size2).add(position2).rotateAround(position2, -this.drehung);
  }

  getEckenV2(): Vector2[] {
    const corners = [v2(-0.5, -0.5), v2(0.5, -0.5), v2(0.5, 0.5), v2(-0.5, 0.5)];
    return corners.map((c) => this.getGridPoint(c));
  }

  getSeitenV2(): Vector2[][] {
    const points = this.getEckenV2();
    return Commons.pairwise(points.concat([points[0]]));
  }

  getSeitenMiddlesV2(): Vector2[] {
    const middles = [v2(-0.5, 0), v2(0.5, 0), v2(0, -0.5), v2(0, 0.5)];
    return middles.map((c) => this.getGridPoint(c));
  }

  clone() {
    return new Aufbau(this.parent, {
      Key: Commons.createUniqueIdString(),
      aufbauMeta: this.aufbauMeta,
      position: this.position.clone(),
      size: this.size,
      drehung: this.drehung,
      fixedSize: this.fixedSize,
    });
  }

  toJSON(): any {
    const json = {
      Key: this.Key,
      aufbauMeta: this.aufbauMeta.Key,
      position: this.position.toJSON(),
      size: this.size.toJSON(),
      drehung: this.drehung,
    };
    return json;
  }

  fromJSON(json: any) {
    if (json) {
      this.Key = json.Key;
      this.aufbauMeta = APP.richMetadata.findAnyObjekt(json.aufbauMeta);
      if (this.aufbauMeta == null) return null;
      json.position && this.position.fromJSON(json.position);
      json.size && this.size.fromJSON(json.size);
      this.drehung = json.drehung;
    }
    if (!this.isResizeable()) {
      this.size.copy(this.aufbauMeta.Size3d);
    }
    return this;
  }

  isResizeable() {
    return !this.isTrafo();
  }

  isMauer() {
    return this.Elementverhalten === "Mauer";
  }

  get Elementverhalten() {
    return this.aufbauMeta.Elementverhalten;
  }

  getSize(): Vector3 {
    return this.size;
  }

  getBox(): Box3 {
    if (this.isMauer()) {
      const underground = 1; // 1m in den Boden
      return new Box3().setFromCenterAndSize(
        v3(0, (this.size.y - underground) / 2, 0),
        v3(this.size.x, this.size.y + underground, this.size.z)
      );
    }
    return new Box3().setFromCenterAndSize(v3(0, this.size.y / 2, 0), this.size.clone());
  }

  getMassMinMaxBox3(): Box3 {
    return new Box3(this.aufbauMeta.MinSize3d ?? v3(0.1, 0.1, 0.1), v3(20, 20, 20));
  }

  calculateContactPositions(): Vector3[] {
    const obb = this.getOBBWorld();
    const inverted = this.matrixWorld.clone().invert();
    return APP.arrangement.ecken
      .filter(
        (e) => e.anMauer && obb.intersectsSphere(new Sphere(v3(e.position), pfostenbreiteCirca))
      )
      .map((e) => v3(e.position).applyMatrix4(inverted));
  }

  get logKey() {
    return this.aufbauMeta?.Key + "-" + this.Key;
  }
}
